import { IHeaderAngularComp } from '@ag-grid-community/angular';
import { IHeaderParams, SortDirection } from '@ag-grid-community/core';
import { NgComponentOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  Type,
  viewChild,
} from '@angular/core';

export interface AgCustomHeaderParams {
  innerComponent?: Type<unknown>;
  // this will be passed to the inner component
  // make sure to use the same name as the input in the inner component
  // if you need the params from the header component, define params: {}.
  innerComponentInputs?: Record<string, unknown>;
}

type AgCustomHeaderParamsWithHeaderParams = AgCustomHeaderParams & IHeaderParams;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet],
  styles: [
    `
      :host {
        display: flex;
        justify-content: space-between;
        flex-direction: row-reverse;
        align-items: center;
        height: 100%;
        width: 100%;
      }
    `,
  ],
  template: `
    @if (isFilterAllowed()) {
      <span
        ref="eMenu"
        class="ag-header-icon ag-header-cell-menu-button ag-header-menu-always-show"
        aria-hidden="true"
        (click)="onFilterClick($event)"
        #filterIcon
      >
        <span class="ag-icon ag-icon-menu" unselectable="on" role="presentation"></span>
      </span>
    }

    <div ref="eLabel" class="ag-header-cell-label" role="presentation" (click)="toggleSort()">
      @if (params.innerComponent) {
        <ng-container
          [ngComponentOutlet]="params.innerComponent"
          [ngComponentOutletInputs]="inputs()"
        />
      } @else {
        <span class="ag-header-cell-text">
          {{ params.displayName }}
        </span>
      }

      @if (isFilterSet()) {
        <span
          ref="eFilter"
          class="ag-header-icon ag-header-label-icon ag-filter-icon"
          aria-hidden="true"
        >
          <span class="ag-icon ag-icon-filter" unselectable="on" role="presentation"></span>
        </span>
      }

      @if (params.enableSorting) {
        <span class="ag-sort-indicator-container" ref="eSortIndicator">
          <span
            ref="eSortOrder"
            class="ag-sort-indicator-icon ag-sort-order ag-hidden"
            aria-hidden="true"
          >
            {{ sortIndex() }}
          </span>
          <span
            ref="eSortAsc"
            class="ag-sort-indicator-icon ag-sort-ascending-icon"
            aria-hidden="true"
            [class.ag-hidden]="currentSort() !== 'asc'"
          >
            <span class="ag-icon ag-icon-asc" unselectable="on" role="presentation"></span>
          </span>
          <span
            ref="eSortDesc"
            class="ag-sort-indicator-icon ag-sort-descending-icon"
            [class.ag-hidden]="currentSort() !== 'desc'"
            aria-hidden="true"
          >
            <span class="ag-icon ag-icon-desc" unselectable="on" role="presentation"></span>
          </span>
          <!-- these api's could be used in future. But for now we are not using them -->
          <!-- <span
            ref="eSortMixed"
            class="ag-sort-indicator-icon ag-sort-mixed-icon ag-hidden"
            aria-hidden="true"
          >
            <span class="ag-icon ag-icon-none" unselectable="on" role="presentation"></span>
          </span> -->
          <!-- <span
            ref="eSortNone"
            class="ag-sort-indicator-icon ag-sort-none-icon ag-hidden"
            aria-hidden="true"
          >
            <span class="ag-icon ag-icon-none" unselectable="on" role="presentation"></span>
          </span> -->
        </span>
      }
    </div>
  `,
})
export class AgCustomHeader implements IHeaderAngularComp, OnDestroy {
  changeDetector = inject(ChangeDetectorRef);
  destroyRef = inject(DestroyRef);

  currentSort = signal<null | SortDirection>(null);
  inputs = signal<Record<string, unknown>>({});
  isFilterAllowed = signal(false);
  isFilterSet = signal(false);
  sortIndex = signal<null | number>(null);

  filterIcon = viewChild<ElementRef<HTMLSpanElement>>('filterIcon');

  params!: AgCustomHeaderParamsWithHeaderParams;

  ngOnDestroy(): void {
    this.params.api.removeEventListener('sortChanged', this.sortChangedListener);
    this.params.api.removeEventListener('filterChanged', this.filterChangedListener);
  }

  agInit(params: AgCustomHeaderParamsWithHeaderParams): void {
    this.params = params;
    this.currentSort.set(params.column.getSort() || null);

    this.isFilterAllowed.set(params.column.isFilterAllowed());

    const innerComponentInputs = params.innerComponentInputs || {};

    this.inputs.set({
      ...innerComponentInputs,
      // if the inner component doesn't define params as input
      // and we pass it anyway it will throw an error
      // so we need to check if the inner component has params as input,
      // if it is defined like params: {}, we are passing the whole params object
      ...(innerComponentInputs.params ? { params } : {}),
    });

    params.api.addEventListener('sortChanged', this.sortChangedListener);
    params.api.addEventListener('filterChanged', this.filterChangedListener);
  }

  filterChangedListener = () => {
    const model = this.params.api.getFilterModel();
    const columnId = this.params.column.getColId();

    const filterModel = model[columnId];

    this.isFilterSet.set(!!filterModel);
  };

  onFilterClick(event: MouseEvent) {
    event.stopPropagation();
    this.params.showColumnMenu(this.filterIcon()!.nativeElement);
  }

  refresh(): boolean {
    return false;
  }

  sortChangedListener = () => {
    this.sortIndex.set(this.params.column.getSortIndex() ?? null);
    this.currentSort.set(this.params.column.getSort() || null);
  };

  toggleSort() {
    if (!this.params.enableSorting) {
      return;
    }

    this.params.progressSort();
    this.currentSort.set(this.params.column.getSort() || null);
  }
}
